import React from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Coins, Plus, TrendingUp, RefreshCw, Infinity } from 'lucide-react';
import { useCredits } from '@/hooks/useCredits';
import { useUserPlan } from '@/hooks/useUserPlan';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useIsMaster } from '@/hooks/useIsMaster';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface CreditsProgressBarProps {
  compact?: boolean;
  showBuyButton?: boolean;
}

// Teto de créditos por plano
const getPlanCreditCeiling = (planType: string | null | undefined): number => {
  switch (planType) {
    case 'basico':
      return 300;
    case 'essencial':
      return 800;
    case 'empresarial':
      return 1500;
    case 'gratuito':
    default:
      return 30;
  }
};

export const CreditsProgressBar: React.FC<CreditsProgressBarProps> = ({ 
  compact = false,
  showBuyButton = true 
}) => {
  const { extraCredits, totalAvailable, loading, refreshCredits } = useCredits();
  const { plan } = useUserPlan();
  const { isAdmin } = useAdminAuth();
  const { isMaster } = useIsMaster();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  
  const isUnlimited = isAdmin || isMaster;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshCredits();
      toast.success('Créditos atualizados!');
    } catch (error) {
      toast.error('Erro ao atualizar créditos');
    } finally {
      setIsRefreshing(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse bg-muted h-16 rounded-lg" />;
  }

  // Admins e Masters têm créditos ilimitados
  if (isUnlimited) {
    if (compact) {
      return (
        <div className="flex items-center gap-2 bg-background/50 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-green-500/50">
          <Infinity className="h-4 w-4 text-green-500" />
          <span className="text-sm font-bold text-green-600">
            Créditos ilimitados ✨
          </span>
        </div>
      );
    }

    return (
      <Card className="p-4 border-green-500/50">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-950">
            <Infinity className="h-5 w-5 text-green-500" />
          </div>
          <div>
            <h3 className="font-semibold text-sm text-green-600">Créditos Ilimitados</h3>
            <p className="text-xs text-muted-foreground">
              {isAdmin ? '👑 Administrador' : '🛠️ Master'} - Acesso total
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Calcular teto dinâmico baseado no plano
  const planCeiling = getPlanCreditCeiling(plan?.plan_type);
  
  // Fórmula: (SaldoAtual / Math.max(SaldoAtual, TetoDoPlano)) * 100
  // Garante que nunca estoure 100%, mesmo com bônus extras
  const effectiveCeiling = Math.max(totalAvailable, planCeiling);
  const percentageRemaining = effectiveCeiling > 0 
    ? (totalAvailable / effectiveCeiling) * 100 
    : 0;

  // Cores baseadas em porcentagem do teto
  // Azul (cheio) → Amarelo (médio) → Vermelho (baixo)
  const percentageOfCeiling = planCeiling > 0 ? (totalAvailable / planCeiling) * 100 : 0;
  const isCriticalCredits = percentageOfCeiling < 20;
  const isLowCredits = percentageOfCeiling >= 20 && percentageOfCeiling < 50;
  const isHealthy = percentageOfCeiling >= 50;

  // Determinar cor do texto
  const getStatusColor = () => {
    if (isCriticalCredits) return 'text-red-500';
    if (isLowCredits) return 'text-yellow-500';
    return 'text-blue-500';
  };

  // Cor da barra de progresso: Azul → Vermelho
  const getProgressClass = () => {
    if (isCriticalCredits) return '[&>div]:bg-red-500';
    if (isLowCredits) return '[&>div]:bg-yellow-500';
    return '[&>div]:bg-blue-500';
  };

  // Cor de fundo da barra
  const getBackgroundClass = () => {
    if (isCriticalCredits) return 'bg-red-100 dark:bg-red-950';
    if (isLowCredits) return 'bg-yellow-100 dark:bg-yellow-950';
    return 'bg-blue-100 dark:bg-blue-950';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 bg-background/50 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-border/50">
        <Coins className={`h-4 w-4 ${getStatusColor()}`} />
        <div className="flex flex-col min-w-[100px]">
          <span className={`text-sm font-bold ${isCriticalCredits ? 'text-red-500' : isLowCredits ? 'text-yellow-600' : 'text-blue-600'}`}>
            {totalAvailable} créditos disponíveis
          </span>
          <Progress 
            value={Math.min(100, percentageRemaining)} 
            className={`h-1 ${getBackgroundClass()} ${getProgressClass()}`}
          />
        </div>
      </div>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${isCriticalCredits ? 'bg-red-100 dark:bg-red-950' : isLowCredits ? 'bg-yellow-100 dark:bg-yellow-950' : 'bg-blue-100 dark:bg-blue-950'}`}>
            <Coins className={`h-5 w-5 ${getStatusColor()}`} />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Créditos Disponíveis</h3>
            <p className="text-xs text-muted-foreground">
              Você tem <span className={`font-bold ${getStatusColor()}`}>{totalAvailable}</span> créditos disponíveis
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {extraCredits > 0 && (
            <Badge variant="secondary" className="gap-1">
              <TrendingUp className="h-3 w-3" />
              +{extraCredits} bônus
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-8 w-8"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Progress 
          value={Math.min(100, percentageRemaining)} 
          className={`h-2 ${getBackgroundClass()} ${getProgressClass()}`}
        />
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{totalAvailable} de {planCeiling} créditos do plano</span>
          <span>
            {isCriticalCredits && '🚨 Crítico'}
            {isLowCredits && '⚠️ Baixo'}
            {isHealthy && '✅ OK'}
          </span>
        </div>
      </div>

      {((isCriticalCredits || isLowCredits) && showBuyButton) && (
        <div className="mt-3 pt-3 border-t">
          <p className="text-xs text-muted-foreground mb-2">
            {isCriticalCredits 
              ? '🚨 Seus créditos estão acabando! Compre mais para continuar usando.' 
              : '⚠️ Você está ficando sem créditos.'}
          </p>
          <Button 
            size="sm" 
            className="w-full gap-2"
            onClick={() => navigate('/creditos')}
            variant={isCriticalCredits ? 'default' : 'outline'}
          >
            <Plus className="h-4 w-4" />
            Comprar Créditos Extras
          </Button>
        </div>
      )}

    </Card>
  );
};
